import { FormEvent, useEffect, useState } from 'react';
import {
  createCustomerDeployment,
  getCustomerDeploymentConfig,
  getCurrentCustomerDeployment,
  getCustomerDeploymentStatus,
  type CustomerDeploymentConfig,
  type CustomerDeploymentStatus,
} from './api.js';

const teamIdentifier = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/u;

type VisibleCustomerDeployment = CustomerDeploymentStatus & {
  quickCreateUrl?: string;
};

export function CustomerDeploymentDialog({
  onClose,
  onOpenJobs,
  onManagePolicy,
}: {
  onClose: () => void;
  onOpenJobs?: (installation: CustomerDeploymentStatus) => void;
  onManagePolicy?: (installation: CustomerDeploymentStatus) => void;
}) {
  const [config, setConfig] = useState<CustomerDeploymentConfig | null>(null);
  const [teamId, setTeamId] = useState('');
  const [prepared, setPrepared] = useState<VisibleCustomerDeployment | null>(null);
  const [deploymentStatus, setDeploymentStatus] = useState<CustomerDeploymentStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [awsOpened, setAwsOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getCustomerDeploymentConfig()
      .then((value) => {
        if (active) setConfig(value);
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    void getCurrentCustomerDeployment()
      .then((value) => {
        if (!active || !value) return;
        setTeamId(value.teamId);
        setPrepared(value);
        setDeploymentStatus(value);
        setStatusError(value.statusStale ? 'Live status is temporarily unavailable.' : null);
      })
      .catch((reason: Error) => {
        if (active) setStatusError(reason.message);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  useEffect(() => {
    if (!prepared) return;
    let active = true;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const value = await getCustomerDeploymentStatus(prepared.installationId);
        if (!active) return;
        setDeploymentStatus(value);
        setStatusError(value.statusStale ? 'Live status is temporarily unavailable.' : null);
        if (value.status === 'expired') return;
      } catch (reason) {
        if (!active) return;
        setStatusError(reason instanceof Error ? reason.message : String(reason));
      }
      if (active) timer = window.setTimeout(poll, 5_000);
    };
    void poll();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [prepared]);

  const normalizedTeamId = teamId.trim();
  const teamIdValid = teamIdentifier.test(normalizedTeamId);
  const connected = deploymentStatus?.status === 'connected';
  const disconnected = deploymentStatus?.status === 'disconnected';
  const expired = deploymentStatus?.status === 'expired';
  const agentLoopReady = deploymentStatus?.agentLoopStatus === 'ready';
  const agentLoopNeedsKey = deploymentStatus?.agentLoopStatus === 'needs_openai_key';
  const restored = Boolean(prepared && !prepared.quickCreateUrl);

  const prepare = async (event: FormEvent) => {
    event.preventDefault();
    if (!config?.enabled || !teamIdValid || busy) return;
    setBusy(true);
    setError(null);
    setPrepared(null);
    setDeploymentStatus(null);
    setStatusError(null);
    setAwsOpened(false);
    try {
      setPrepared(await createCustomerDeployment(normalizedTeamId));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  };

  const resetPrepared = () => {
    setPrepared(null);
    setDeploymentStatus(null);
    setStatusError(null);
    setAwsOpened(false);
  };

  return (
    <div
      className="deployment-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="deployment-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-deployment-title"
      >
        <header className="deployment-heading">
          <div className="deployment-heading-copy">
            <div className="deployment-kicker">
              <span className="deployment-aws-mark" aria-hidden="true">AWS</span>
              <span>Customer-managed infrastructure</span>
              <span className={`deployment-status ${config?.enabled ? 'ready' : ''}`}>
                <i />
                {!config
                  ? 'Checking'
                  : connected
                    ? 'Connected'
                    : disconnected
                      ? 'Reconnecting'
                      : expired
                        ? 'Link expired'
                        : config.enabled
                          ? 'Ready'
                          : 'Unavailable'}
              </span>
            </div>
            <h2 id="customer-deployment-title">{connected
              ? 'AWS account connected'
              : disconnected
                ? 'AWS connection interrupted'
                : expired
                  ? 'Deployment link expired'
                  : 'Connect your AWS account'}</h2>
            <p>{connected
              ? agentLoopReady
                ? 'Your customer-managed broker and private agent runtime are online.'
                : 'Your customer-managed infrastructure is online; the private agent runtime still needs attention.'
              : disconnected
                ? 'Cyrus is waiting for the credential broker in your VPC to reconnect.'
                : expired
                  ? 'Create a fresh one-time link when you are ready to try the deployment again.'
                  : 'Launch a reviewed CloudFormation stack that gives Cyrus secure, policy-controlled access to your private workloads.'}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close AWS deployment">×</button>
        </header>

        <ol className="deployment-progress" aria-label="AWS connection progress">
          <li className={prepared ? 'complete' : 'current'}>
            <i>{prepared ? '✓' : '1'}</i>
            <span><strong>Configure</strong><small>Identify your team</small></span>
          </li>
          <li className={connected ? 'complete' : prepared ? 'current' : ''}>
            <i>{connected ? '✓' : '2'}</i>
            <span><strong>Review in AWS</strong><small>Approve the stack</small></span>
          </li>
          <li className={connected ? 'complete current' : ''}>
            <i>{connected ? '✓' : '3'}</i>
            <span><strong>Connect</strong><small>Enroll automatically</small></span>
          </li>
        </ol>

        <div className="deployment-layout">
          <form className="deployment-form" onSubmit={prepare} aria-busy={busy}>
            <div className="deployment-section-heading">
              <span className="eyebrow">{connected
                ? 'AWS account connected'
                : disconnected
                  ? 'Connection interrupted'
                  : prepared
                    ? restored ? 'Deployment state restored' : 'Deployment link ready'
                    : 'Deployment details'}</span>
              <h3>{connected
                ? 'Infrastructure is ready'
                : disconnected
                  ? 'The broker is temporarily offline'
                  : prepared
                    ? restored ? 'Deployment is still being monitored' : 'Continue in AWS'
                    : 'Choose where this connection belongs'}</h3>
              <p>{connected
                ? agentLoopReady
                  ? 'Cyrus received healthy heartbeats from both private services in your VPC.'
                  : 'The credential broker is connected. Cyrus is still checking the private agent runtime.'
                : disconnected
                  ? 'The stack enrolled successfully, but its outbound control connection is not currently healthy.'
                  : prepared
                    ? restored
                      ? 'Cyrus recovered this installation from server-side state and will continue checking it automatically.'
                      : 'The button below opens AWS with every required parameter pre-filled.'
                    : 'This identifier scopes the resources, credentials, and policies created for your team.'}</p>
            </div>

            <label className="deployment-team-field">
              <span>Team identifier</span>
              <input
                aria-label="Team identifier"
                aria-describedby="team-identifier-help"
                aria-invalid={Boolean(normalizedTeamId && !teamIdValid)}
                autoComplete="organization"
                autoFocus
                maxLength={64}
                pattern="[A-Za-z0-9][A-Za-z0-9_-]{0,63}"
                placeholder="acme-platform"
                required
                disabled={connected}
                value={teamId}
                onChange={(event) => {
                  setTeamId(event.target.value);
                  resetPrepared();
                }}
              />
              <small id="team-identifier-help">
                {normalizedTeamId && !teamIdValid
                  ? 'Start with a letter or number; use only letters, numbers, hyphens, or underscores.'
                  : 'Used only to namespace this team’s customer-owned AWS resources.'}
              </small>
            </label>

            <div className="deployment-facts">
              <div><span>Region</span><strong>{deploymentStatus?.region ?? config?.region ?? 'Checking…'}</strong></div>
              <div><span>Status</span><strong>{connected ? 'Connected' : disconnected ? 'Reconnecting' : 'About 10 minutes'}</strong></div>
              <div><span>{connected ? 'Stack' : 'Secure link'}</span><strong>{connected
                ? deploymentStatus.stackName ?? prepared?.stackName
                : config ? `${config.installationTtlMinutes} min expiry` : 'Checking…'}</strong></div>
            </div>

            {!config && !error && <div className="deployment-loading"><i /><span>Checking deployment readiness…</span></div>}
            {config && !config.enabled && (
              <div className="deployment-warning" role="status">
                <strong>Deployment is not enabled here</strong>
                <span>{config.unavailableReason}</span>
              </div>
            )}
            {error && <div className="deployment-warning error" role="alert">{error}</div>}

            {connected ? (
              <div className="deployment-connected-card" role="status" aria-live="polite">
                <div className="deployment-connected-heading">
                  <span className="connected-check">✓</span>
                  <div>
                    <strong>Infrastructure is online</strong>
                    <small>{agentLoopReady
                      ? 'Your private broker and OpenAI Agents SDK loop are ready.'
                      : agentLoopNeedsKey
                        ? 'The private agent loop is connected and waiting for its customer-owned OpenAI key.'
                        : 'Your private broker is ready; the agent loop is still connecting.'}</small>
                  </div>
                </div>
                <dl>
                  {deploymentStatus.accountId && <div><dt>AWS account</dt><dd>{deploymentStatus.accountId}</dd></div>}
                  {deploymentStatus.vpcId && <div><dt>VPC</dt><dd>{deploymentStatus.vpcId}</dd></div>}
                  {deploymentStatus.lastSeenAt && <div><dt>Last heartbeat</dt><dd>{formatHeartbeat(deploymentStatus.lastSeenAt)}</dd></div>}
                  <div>
                    <dt>Agent loop</dt>
                    <dd>{agentLoopReady
                      ? 'Ready'
                      : agentLoopNeedsKey
                        ? 'OpenAI key required'
                        : 'Connecting'}</dd>
                  </div>
                </dl>
                <div className="deployment-next-step">
                  <span>{agentLoopReady ? 'Recommended next step' : 'Action required'}</span>
                  <strong>{agentLoopReady
                    ? 'Configure repository access, then publish an encrypted agent session.'
                    : agentLoopNeedsKey
                      ? 'Add {"api_key":"…"} to the stack’s AgentLoopOpenAISecret in AWS Secrets Manager.'
                      : 'Wait for the agent-loop instance to establish its outbound connection.'}</strong>
                </div>
                <div className="deployment-connected-actions">
                  <button
                    type="button"
                    className="deployment-primary"
                    onClick={() => onManagePolicy?.(deploymentStatus)}
                  >
                    Configure GitHub access <span aria-hidden="true">→</span>
                  </button>
                  <button
                    type="button"
                    disabled={!agentLoopReady}
                    onClick={() => onOpenJobs?.(deploymentStatus)}
                  >
                    Open agent sessions
                  </button>
                </div>
              </div>
            ) : disconnected ? (
              <div className="deployment-interrupted" role="alert">
                <span className="deployment-reconnect-pulse" />
                <div>
                  <strong>Reconnecting to the broker…</strong>
                  <small>Cyrus will recover automatically. No inbound VPC access is required.</small>
                </div>
              </div>
            ) : expired ? (
              <div className="deployment-expired" role="status">
                <strong>Deployment link expired</strong>
                <span>No broker enrolled with this one-time key. Create a new link to try again.</span>
                <button type="button" onClick={resetPrepared}>Create a new link</button>
              </div>
            ) : !prepared ? (
              <>
                <button
                  className="deployment-primary"
                  type="submit"
                  disabled={!config?.enabled || !teamIdValid || busy}
                >
                  {busy
                    ? <><span className="deployment-spinner" />Creating secure link…</>
                    : <>Create deployment link <span aria-hidden="true">→</span></>}
                </button>
                <p className="deployment-approval-note">
                  <span aria-hidden="true">✓</span>
                  Nothing is created until you review and approve it in AWS.
                </p>
              </>
            ) : prepared.quickCreateUrl ? (
              <div className="deployment-ready" role="status" aria-live="polite">
                <div className="deployment-ready-heading">
                  <span className="ready-check">✓</span>
                  <div>
                    <strong>Secure link created</strong>
                    <small>Expires {formatExpiry(prepared.expiresAt)} · one-time enrollment</small>
                  </div>
                </div>
                <a
                  href={prepared.quickCreateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setAwsOpened(true)}
                >
                  Review and deploy in AWS
                  <span aria-hidden="true">↗</span>
                </a>
                <p>AWS will show the full resource list and IAM capabilities before deployment begins.</p>
                {awsOpened && deploymentStatus?.status === 'pending' && (
                  <div className="deployment-watch">
                    <span className="deployment-spinner dark" />
                    <span><strong>Waiting for AWS deployment</strong><small>This page updates automatically after the broker enrolls.</small></span>
                  </div>
                )}
                {statusError && (
                  <div className="deployment-status-delay">
                    Status check delayed. Retrying automatically.
                  </div>
                )}
              </div>
            ) : (
              <div className="deployment-restored" role="status" aria-live="polite">
                <div>
                  <span className="deployment-reconnect-pulse" />
                  <span>
                    <strong>Monitoring the saved installation</strong>
                    <small>This state came from the server, not browser storage.</small>
                  </span>
                </div>
                <p>The original one-time AWS link is intentionally not persisted. If that deployment was not submitted, create a fresh link.</p>
                <button type="button" onClick={resetPrepared}>Create a fresh deployment link</button>
                {statusError && (
                  <div className="deployment-status-delay">
                    Status check delayed. Retrying automatically.
                  </div>
                )}
              </div>
            )}
          </form>

          <aside className="deployment-review" aria-label="Deployment summary">
            <div className="deployment-review-heading">
              <span className="eyebrow">Private network path</span>
              <h3>What gets installed</h3>
              <p>Outbound traffic is forced through a broker inside a dedicated VPC.</p>
            </div>

            <div
              className="deployment-path"
              role="img"
              aria-label="MicroVM traffic flows through the private broker to approved destinations"
            >
              <div><i className="path-workload" /><span><strong>MicroVM</strong><small>Workload</small></span></div>
              <b aria-hidden="true">→</b>
              <div><i className="path-broker" /><span><strong>Broker</strong><small>Private EC2</small></span></div>
              <b aria-hidden="true">→</b>
              <div><i className="path-egress" /><span><strong>Egress</strong><small>Policy allowed</small></span></div>
            </div>

            <ul className="deployment-approvals">
              <li><i>01</i><div><strong>Dedicated VPC</strong><span>Private broker and isolated MicroVM subnets.</span></div></li>
              <li><i>02</i><div><strong>Customer-owned trust</strong><span>Your account generates and stores the CA and credentials.</span></div></li>
              <li><i>03</i><div><strong>Customer pull connector</strong><span>Your scheduled Lambda fetches policy and applies it with customer-local IAM.</span></div></li>
              <li><i>04</i><div><strong>Private agent loop</strong><span>The OpenAI Agents SDK runs in your VPC and reads only your customer-owned API key.</span></div></li>
            </ul>

            <div className="deployment-boundary">
              <div><span className="secure-dot" /><strong>Cyrus can</strong><span>publish signed, non-secret desired policy</span></div>
              <div><span className="boundary-block" /><strong>Cyrus cannot</strong><span>assume the connector role or receive credential values</span></div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function formatExpiry(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatHeartbeat(value: string): string {
  const age = Date.now() - new Date(value).getTime();
  if (age >= 0 && age < 60_000) return 'Just now';
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}
